import React, { useState, useEffect, useCallback } from 'react';
import { Shield, KeyRound, X, RefreshCw, Navigation, CheckSquare, Square, Calendar } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const DEFAULT_MODULES = [
  { id: 'runsheet', nameMr: 'दैनिक दूध वितरण / वाटप (Run-Sheet)', nameEn: 'Daily Run-Sheet (Delivery)' },
  { id: 'pos', nameMr: 'काउंटर POS विक्री', nameEn: 'Counter POS Sales' },
  { id: 'customers', nameMr: 'ग्राहक नोंदणी', nameEn: 'Customer Master' },
  { id: 'products', nameMr: 'उत्पादने कॅटलॉग', nameEn: 'Product Catalog' },
  { id: 'rate_chart', nameMr: 'दूध दरपत्रक (Line Rate Charts)', nameEn: 'Milk Rate Charts' },
  { id: 'billing', nameMr: 'मासिक खातावही', nameEn: 'Monthly Ledger' },
  { id: 'payments', nameMr: 'पेमेंट वसुली व जमा पावत्या', nameEn: 'Payment Collection & Receipts' }
];

export default function UserManagement({ currentTenant, lang = 'mr' }) {
  const isMr = lang === 'mr';

  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [users, setUsers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [_centers, setCenters] = useState([]);
  const [lines, setLines] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [_maxUsersAllowed, setMaxUsersAllowed] = useState(2);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  // Edit / Password Modal State
  const [modalMode, setModalMode] = useState(null); // 'permissions' or 'password'
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    role: 'DELIVERY',
    center_access: 'ALL',
    assigned_line_id: '',
    assigned_line_ids: [],
    can_backdate_crud: false,
    permissions: {},
    password: '',
    confirmPassword: ''
  });

  const [submitting, setSubmitting] = useState(false);

  // Load Staff
  const loadStaff = useCallback(async () => {
    if (!tenantSlug) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/staff`);
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
        setCenters(data.centers || []);
        setLines(data.lines || []);
        if (data.max_users_allowed) {
          setMaxUsersAllowed(data.max_users_allowed);
        }
      } else {
        setError(data.message || 'Error loading staff');
      }
    } catch (err) {
      setError(err.message || 'Server connection error');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  // Open Permissions Modal
  const openPermissionsModal = (user) => {
    setSelectedUser(user);

    // Parse assigned lines (supports array or legacy single line)
    let userLines = [];
    if (user.assigned_line_ids) {
      try {
        userLines = typeof user.assigned_line_ids === 'string' 
          ? JSON.parse(user.assigned_line_ids) 
          : user.assigned_line_ids;
      } catch {
        userLines = [];
      }
    } else if (user.assigned_line_id) {
      userLines = [parseInt(user.assigned_line_id)];
    }

    // Parse permissions
    let userPerms = {};
    if (user.permissions) {
      try {
        userPerms = typeof user.permissions === 'string' 
          ? JSON.parse(user.permissions) 
          : user.permissions;
      } catch {
        userPerms = {};
      }
    } else {
      // Default permissions based on role
      DEFAULT_MODULES.forEach(m => {
        userPerms[m.id] = {
          view: true,
          add: user.role !== 'DELIVERY' || m.id === 'runsheet',
          modify: user.role === 'ADMIN' || m.id === 'runsheet',
          delete: user.role === 'ADMIN'
        };
      });
    }

    setFormData({
      role: user.role || 'DELIVERY',
      center_access: user.center_access || 'ALL',
      assigned_line_id: user.assigned_line_id || '',
      assigned_line_ids: Array.isArray(userLines) ? userLines : [],
      can_backdate_crud: Boolean(user.can_backdate_crud),
      can_modify_rate: Boolean(user.can_modify_rate || userPerms?.runsheet?.modify_rate),
      permissions: userPerms,
      password: '',
      confirmPassword: ''
    });
    setModalMode('permissions');
  };

  // Open Password Modal
  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setFormData({
      password: '',
      confirmPassword: ''
    });
    setModalMode('password');
  };

  // Toggle Line selection
  const toggleLine = (lineId) => {
    const id = parseInt(lineId);
    setFormData(prev => {
      const current = prev.assigned_line_ids || [];
      const updated = current.includes(id) 
        ? current.filter(x => x !== id) 
        : [...current, id];
      return {
        ...prev,
        assigned_line_ids: updated,
        assigned_line_id: updated[0] || null
      };
    });
  };

  // Toggle CRUD Permission
  const togglePerm = (moduleId, action) => {
    setFormData(prev => {
      const perms = { ...(prev.permissions || {}) };
      const mod = { ...(perms[moduleId] || { view: true, add: false, modify: false, delete: false }) };
      mod[action] = !mod[action];
      // If add or modify is enabled, view must be enabled
      if (mod.add || mod.modify || mod.delete) mod.view = true;
      perms[moduleId] = mod;
      return { ...prev, permissions: perms };
    });
  };

  // Apply preset permissions
  const applyPreset = (preset) => {
    const newPerms = {};
    DEFAULT_MODULES.forEach(m => {
      if (preset === 'ADMIN') {
        newPerms[m.id] = { view: true, add: true, modify: true, delete: true };
      } else if (preset === 'OPERATOR') {
        newPerms[m.id] = { view: true, add: true, modify: true, delete: false };
      } else if (preset === 'DELIVERY') {
        newPerms[m.id] = {
          view: m.id === 'runsheet' || m.id === 'customers',
          add: m.id === 'runsheet',
          modify: m.id === 'runsheet',
          delete: false
        };
      } else if (preset === 'VIEW_ONLY') {
        newPerms[m.id] = { view: true, add: false, modify: false, delete: false };
      }
    });
    setFormData(prev => ({
      ...prev,
      permissions: newPerms,
      can_backdate_crud: preset === 'ADMIN'
    }));
  };

  // Submit Permissions or Password
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modalMode === 'password') {
      if (formData.password.length < 4) {
        alert(isMr ? 'पासवर्ड किमान ४ अक्षरांचा असावा.' : 'Password must be at least 4 characters.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        alert(isMr ? 'दोन्ही पासवर्ड जुळत नाहीत.' : 'Passwords do not match.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = modalMode === 'password'
        ? { password: formData.password }
        : {
            role: formData.role,
            center_access: formData.center_access,
            assigned_line_id: formData.assigned_line_ids[0] || null,
            assigned_line_ids: formData.assigned_line_ids,
            can_backdate_crud: formData.can_backdate_crud,
            can_modify_rate: formData.can_modify_rate,
            permissions: formData.permissions
          };

      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/staff/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setMsg(data.message || (isMr ? 'हक्क अद्ययावत केले!' : 'Permissions updated!'));
        setModalMode(null);
        loadStaff();
      } else {
        alert(data.message || 'Error updating');
      }
    } catch (err) {
      alert(err.message || 'Error updating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a', margin: 0 }}>
            {isMr ? 'कर्मचारी व ऑपरेटिंग हक्क व्यवस्थापन' : 'Staff & Operating Permissions'}
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>
            {isMr 
              ? 'कर्मचाऱ्यांना मल्टिपल रूट्सचे वाटप करा, फॉर्मनुसार CRUD हक्क आणि मागील तारखेच्या नोंदींची परवानगी नियंत्रित करा' 
              : 'Assign multiple routes, granular CRUD form permissions, and back-date entry controls'}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={loadStaff}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
          >
            <RefreshCw size={14} />
            <span>{isMr ? 'रिफ्रेश' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      {msg && (
        <div style={{ padding: '0.75rem 1rem', borderRadius: 8, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.85rem', fontWeight: 700 }}>
          {msg}
        </div>
      )}
      {error && (
        <div style={{ padding: '0.75rem 1rem', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecdd3', fontSize: '0.85rem', fontWeight: 700 }}>
          {error}
        </div>
      )}

      {/* Staff Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        {loading ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            {isMr ? 'कर्मचारी यादी लोड होत आहे...' : 'Loading staff...'}
          </div>
        ) : users.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            {isMr ? 'कोणतेही कर्मचारी आढळले नाहीत.' : 'No staff found.'}
          </div>
        ) : (
          users.map(u => {
            // Assigned lines array
            let assignedLinesList = [];
            if (u.assigned_line_ids) {
              try {
                const parsed = typeof u.assigned_line_ids === 'string' ? JSON.parse(u.assigned_line_ids) : u.assigned_line_ids;
                assignedLinesList = lines.filter(l => parsed.includes(l.id));
              } catch {
                assignedLinesList = [];
              }
            } else if (u.assigned_line_id) {
              const single = lines.find(l => String(l.id) === String(u.assigned_line_id));
              if (single) assignedLinesList = [single];
            }

            return (
              <div key={u.id} style={{ background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{u.name}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>@{u.username} • {u.phone || 'No Phone'}</div>
                    </div>
                    <span style={{
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 6,
                      background: u.role === 'ADMIN' ? '#e0f2fe' : (u.role === 'CASHIER' ? '#fef3c7' : '#dcfce7'),
                      color: u.role === 'ADMIN' ? '#0369a1' : (u.role === 'CASHIER' ? '#b45309' : '#15803d')
                    }}>
                      {u.role}
                    </span>
                  </div>

                  {/* Assigned Routes / Lines */}
                  <div style={{ marginTop: '0.85rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Navigation size={12} color="#0284c7" />
                      <span>{isMr ? 'नियुक्त वितरण मार्ग (Assigned Routes):' : 'Assigned Delivery Routes:'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {assignedLinesList.length === 0 ? (
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {isMr ? 'कोणतीही लाईन नियुक्त नाही (All/None)' : 'No specific lines assigned'}
                        </span>
                      ) : (
                        assignedLinesList.map(l => (
                          <span key={l.id} style={{ fontSize: '0.68rem', fontWeight: 700, background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', padding: '1px 6px', borderRadius: 4 }}>
                            {isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Back-date Badge */}
                  <div style={{ marginTop: '0.65rem' }}>
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: u.can_backdate_crud || u.role === 'ADMIN' ? '#dcfce7' : '#fee2e2',
                      color: u.can_backdate_crud || u.role === 'ADMIN' ? '#15803d' : '#b91c1c',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <Calendar size={11} />
                      <span>
                        {u.can_backdate_crud || u.role === 'ADMIN' 
                          ? (isMr ? '✓ मागील तारखांचे बदल अनुमत (Backdate Allowed)' : '✓ Backdate Allowed') 
                          : (isMr ? '✕ मागील तारखा लॉक (Current Date Only)' : '✕ Current Date Only')}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
                  <button
                    onClick={() => openPermissionsModal(u)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      padding: '0.45rem',
                      borderRadius: 8,
                      border: '1px solid #bae6fd',
                      background: '#f0f9ff',
                      color: '#0284c7',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      cursor: 'pointer'
                    }}
                  >
                    <Shield size={13} />
                    <span>{isMr ? 'हक्क व रूट्स बदला' : 'Edit Rights'}</span>
                  </button>

                  <button
                    onClick={() => openPasswordModal(u)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.45rem 0.65rem',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#475569',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    title={isMr ? 'पासवर्ड बदला' : 'Reset Password'}
                  >
                    <KeyRound size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Permissions Modal */}
      {modalMode === 'permissions' && selectedUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            
            <div style={{ background: '#0284c7', color: '#fff', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 900, fontSize: '1rem' }}>{selectedUser.name}</span>
                <span style={{ fontSize: '0.75rem', color: '#e0f2fe', marginLeft: 8 }}>
                  {isMr ? '- ऑपरेटिंग हक्क व रूट्स वाटप' : '- Operating Rights & Routes'}
                </span>
              </div>
              <button onClick={() => setModalMode(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              {/* Quick Presets */}
              <div style={{ background: '#f8fafc', padding: '0.65rem', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', marginBottom: 6 }}>
                  ⚡ {isMr ? 'झटपट हक्क सेट करा (Quick Presets):' : 'Quick Presets:'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => applyPreset('ADMIN')} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    {isMr ? 'पूर्ण अधिकार (Full Admin)' : 'Full Admin'}
                  </button>
                  <button type="button" onClick={() => applyPreset('OPERATOR')} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    {isMr ? 'ऑपरेटर (Standard CRUD)' : 'Operator'}
                  </button>
                  <button type="button" onClick={() => applyPreset('DELIVERY')} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    {isMr ? 'डिलिव्हरी बॉय (Runsheet Only)' : 'Delivery Only'}
                  </button>
                  <button type="button" onClick={() => applyPreset('VIEW_ONLY')} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    {isMr ? 'फक्त पाहणे (View Only)' : 'View Only'}
                  </button>
                </div>
              </div>

              {/* Section 1: Assigned Multiple Lines */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Navigation size={14} color="#0284c7" />
                  <span>{isMr ? '१. नियुक्त वितरण मार्ग / लाईन्स (Multiple Routes):' : '1. Assigned Delivery Lines:'}</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 6, background: '#f8fafc', padding: '0.65rem', borderRadius: 10, border: '1px solid #e2e8f0', maxHeight: 150, overflowY: 'auto' }}>
                  {lines.map(l => {
                    const isChecked = formData.assigned_line_ids.includes(l.id);
                    return (
                      <div
                        key={l.id}
                        onClick={() => toggleLine(l.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '0.4rem 0.65rem',
                          borderRadius: 6,
                          background: isChecked ? '#e0f2fe' : '#ffffff',
                          border: isChecked ? '1px solid #0284c7' : '1px solid #cbd5e1',
                          cursor: 'pointer'
                        }}
                      >
                        {isChecked ? <CheckSquare size={16} color="#0284c7" /> : <Square size={16} color="#94a3b8" />}
                        <span style={{ fontSize: '0.75rem', fontWeight: isChecked ? 800 : 500, color: isChecked ? '#0369a1' : '#334155' }}>
                          {isMr ? (l.name_mr || l.name) : (l.name_en || l.name)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: Back-date CRUD Toggle */}
              <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={15} />
                    <span>{isMr ? 'मागील तारखेच्या नोंदी करणे (Back-Date CRUD Permission)' : 'Allow Back-Date Entries (CRUD)'}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#15803d', marginTop: 2 }}>
                    {isMr ? 'चालू ठेवल्यास ऑपरेटर मागील तारखांचे वितरण व नोंदी बदलू शकतो. बंद असल्यास फक्त आजची तारीख चालेल.' : 'If enabled, operator can modify past date records. If disabled, past dates are locked.'}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.can_backdate_crud}
                  onChange={e => setFormData({ ...formData, can_backdate_crud: e.target.checked })}
                  style={{ width: 20, height: 20, cursor: 'pointer', accentColor: '#16a34a' }}
                />
              </div>

              
              {/* Dynamic Rate Permission */}
              <div style={{ background: '#fefce8', border: '1.5px solid #fde047', borderRadius: 10, padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.65rem' }}>
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#854d0e', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>💰</span>
                    <span>{isMr ? 'दैनिक दर बदलण्याची परवानगी (Allow Dynamic Rate Edit)' : 'Allow Dynamic Rate Edit'}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#a16207', marginTop: 2 }}>
                    {isMr ? 'हा पर्याय दिल्यास हा ऑपरेटर/डिलिव्हरी बॉय वाटप करताना डायनॅमिक ग्राहकांचा दर बदलू शकेल.' : 'If enabled, operator can adjust milk rate during delivery for dynamic customers.'}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={Boolean(formData.can_modify_rate || formData.permissions?.runsheet?.modify_rate)}
                  onChange={e => {
                    const checked = e.target.checked;
                    setFormData({
                      ...formData,
                      can_modify_rate: checked,
                      permissions: {
                        ...formData.permissions,
                        runsheet: {
                          ...(formData.permissions?.runsheet || {}),
                          modify_rate: checked
                        }
                      }
                    });
                  }}
                  style={{ width: 20, height: 20, cursor: 'pointer', accentColor: '#ca8a04' }}
                />
              </div>

              {/* Section 3: Granular Module CRUD Permissions */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', display: 'block', marginBottom: 6 }}>
                  {isMr ? '२. फॉर्मनुसार ऑपरेटिंग हक्क (Module CRUD Permissions):' : '2. Form-wise Operating Permissions:'}
                </label>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', textAlign: 'center' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                        <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>{isMr ? 'मॉड्यूल / फॉर्म' : 'Module'}</th>
                        <th style={{ padding: '0.5rem' }}>{isMr ? 'पाहणे (View)' : 'View'}</th>
                        <th style={{ padding: '0.5rem' }}>{isMr ? 'जोडणे (Add)' : 'Add'}</th>
                        <th style={{ padding: '0.5rem' }}>{isMr ? 'बदल (Modify)' : 'Modify'}</th>
                        <th style={{ padding: '0.5rem' }}>{isMr ? 'हटवणे (Delete)' : 'Delete'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DEFAULT_MODULES.map(m => {
                        const p = formData.permissions[m.id] || { view: true, add: false, modify: false, delete: false };
                        return (
                          <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 700, color: '#0f172a' }}>
                              {isMr ? m.nameMr : m.nameEn}
                            </td>
                            {['view', 'add', 'modify', 'delete'].map(act => (
                              <td key={act} style={{ padding: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(p[act])}
                                  onChange={() => togglePerm(m.id, act)}
                                  style={{ cursor: 'pointer', accentColor: '#0284c7' }}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700 }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: '0.5rem 1.4rem', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800 }}
                >
                  {submitting ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') : (isMr ? 'हक्क सेव्ह करा' : 'Save Rights')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {modalMode === 'password' && selectedUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 380, width: '100%', overflow: 'hidden' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '1rem', fontWeight: 800 }}>
              {isMr ? `${selectedUser.name} - नवीन पासवर्ड सेट करा` : 'Reset Password'}
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'नवीन पासवर्ड:' : 'New Password:'}
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'पासवर्ड पुन्हा टाइप करा:' : 'Confirm Password:'}
                </label>
                <input
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{ padding: '0.45rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 700 }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: '0.45rem 1.25rem', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800 }}
                >
                  {submitting ? (isMr ? 'बदलत आहे...' : 'Resetting...') : (isMr ? 'पासवर्ड बदला' : 'Reset Password')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
